import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  useAddresses,
  useCreateAddress,
  useDeleteAddress,
  useSetDefaultAddress,
  useAddressLookup,
  type CreateAddressInput,
  type Address,
  type LookupResult,
} from '../hooks/useAddresses'
import { Plus, Trash2, Star, Search, Loader2, MapPin, Building2 } from 'lucide-react'
import { toast } from 'sonner'

const emptyForm: CreateAddressInput = {
  label: '',
  type: 'both',
  name: '',
  addressLine1: '',
  addressLine2: '',
  suburb: '',
  state: '',
  postcode: '',
  email: '',
  phone: '',
}

export function AddressBookPage() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [lookupOpen, setLookupOpen] = useState(false)
  const [form, setForm] = useState<CreateAddressInput>({ ...emptyForm })
  const { data, isLoading, error } = useAddresses()
  const createAddress = useCreateAddress()
  const deleteAddress = useDeleteAddress()
  const setDefault = useSetDefaultAddress()

  const addresses = data?.addresses ?? []

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createAddress.mutate(form, {
      onSuccess: () => {
        setDialogOpen(false)
        setForm({ ...emptyForm })
        toast.success('Address saved')
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to save address')
      },
    })
  }

  const update = (field: keyof CreateAddressInput) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => setForm({ ...form, [field]: e.target.value })

  return (
    <div className="container max-w-5xl py-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Address Book</h1>
          <p className="text-muted-foreground">
            Save addresses for quick reuse when sending letters.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setLookupOpen(true)}>
            <Building2 className="mr-2 h-4 w-4" />
            Look Up Business
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Add Address
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>Add Address</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="label">Label</Label>
                    <Input id="label" placeholder="Home, Office..." value={form.label} onChange={update('label')} required />
                  </div>
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as 'sender' | 'recipient' | 'both' })}>
                      <SelectTrigger id="type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">Both</SelectItem>
                        <SelectItem value="sender">Sender only</SelectItem>
                        <SelectItem value="recipient">Recipient only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" value={form.name} onChange={update('name')} required />
                </div>
                <div>
                  <Label htmlFor="addr1">Address line 1</Label>
                  <Input id="addr1" value={form.addressLine1} onChange={update('addressLine1')} required />
                </div>
                <div>
                  <Label htmlFor="addr2">Address line 2</Label>
                  <Input id="addr2" value={form.addressLine2 ?? ''} onChange={update('addressLine2')} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="suburb">Suburb</Label>
                    <Input id="suburb" value={form.suburb} onChange={update('suburb')} required />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input id="state" value={form.state} onChange={update('state')} placeholder="NSW" required />
                  </div>
                  <div>
                    <Label htmlFor="postcode">Postcode</Label>
                    <Input id="postcode" value={form.postcode} onChange={update('postcode')} placeholder="2000" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email (optional)</Label>
                    <Input id="email" type="email" value={form.email ?? ''} onChange={update('email')} />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone (optional)</Label>
                    <Input id="phone" value={form.phone ?? ''} onChange={update('phone')} />
                  </div>
                </div>
                {createAddress.isError && (
                  <p className="text-sm text-destructive">{createAddress.error.message}</p>
                )}
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createAddress.isPending}>
                    {createAddress.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Address
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Business Address Lookup Dialog */}
      <BusinessLookupDialog
        open={lookupOpen}
        onOpenChange={setLookupOpen}
        onSelect={(result) => {
          setForm({
            ...emptyForm,
            label: result.name,
            name: result.name,
            addressLine1: result.address_line1,
            suburb: result.suburb,
            state: result.state,
            postcode: result.postcode,
            phone: result.phone ?? '',
          })
          setLookupOpen(false)
          setDialogOpen(true)
        }}
      />

      <Card>
        <CardHeader>
          <CardTitle>Saved Addresses</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <p className="text-sm text-destructive">{error.message}</p>
          ) : addresses.length === 0 ? (
            <div className="py-8 text-center">
              <MapPin className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No saved addresses yet.</p>
              <p className="text-sm text-muted-foreground">Add your first address to speed up letter sending.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {addresses.map((addr) => (
                  <AddressRow
                    key={addr.id}
                    address={addr}
                    onDelete={() => deleteAddress.mutate(addr.id, {
                      onSuccess: () => toast.success('Address deleted'),
                      onError: (err) => toast.error(err.message || 'Failed to delete address'),
                    })}
                    onSetDefault={(field) => setDefault.mutate({ id: addr.id, field })}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function AddressRow({
  address,
  onDelete,
  onSetDefault,
}: {
  address: Address
  onDelete: () => void
  onSetDefault: (field: 'sender' | 'recipient') => void
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {address.label}
        <div className="flex gap-1 mt-1">
          {address.is_default_sender && (
            <Badge variant="outline" className="text-xs"><Star className="h-3 w-3 mr-1" />Default sender</Badge>
          )}
          {address.is_default_recipient && (
            <Badge variant="outline" className="text-xs"><Star className="h-3 w-3 mr-1" />Default recipient</Badge>
          )}
        </div>
      </TableCell>
      <TableCell>{address.name}</TableCell>
      <TableCell className="text-sm">
        {address.address_line1}
        <br />
        <span className="text-muted-foreground">{address.suburb} {address.state} {address.postcode}</span>
      </TableCell>
      <TableCell>
        <Badge variant="secondary" className="capitalize">{address.type}</Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex justify-end gap-1">
          {!address.is_default_sender && (address.type === 'sender' || address.type === 'both') && (
            <Button variant="ghost" size="sm" onClick={() => onSetDefault('sender')} title="Set as default sender">
              <Star className="h-4 w-4" />
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete address?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently remove "{address.label}" from your address book.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onDelete}>Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  )
}

function BusinessLookupDialog({
  open,
  onOpenChange,
  onSelect,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (result: LookupResult) => void
}) {
  const [businessName, setBusinessName] = useState('')
  const [location, setLocation] = useState('')
  const lookup = useAddressLookup()

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    lookup.mutate({ q: businessName, location })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Look Up Business Address</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <Label htmlFor="biz-name">Business name</Label>
            <Input id="biz-name" placeholder="e.g. Woolworths" value={businessName} onChange={(e) => setBusinessName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="biz-location">Location</Label>
            <Input id="biz-location" placeholder="e.g. Sydney, Parramatta NSW" value={location} onChange={(e) => setLocation(e.target.value)} required />
          </div>
          <Button type="submit" disabled={lookup.isPending} className="w-full">
            {lookup.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Search
          </Button>
        </form>

        {lookup.isError && (
          <p className="text-sm text-destructive">{lookup.error.message}</p>
        )}

        {lookup.data?.results && lookup.data.results.length > 0 && (
          <div className="space-y-2 mt-4 max-h-64 overflow-y-auto">
            {lookup.data.results.map((result, i) => (
              <button
                key={i}
                type="button"
                onClick={() => onSelect(result)}
                className="w-full text-left rounded-lg border p-3 hover:bg-accent transition-colors"
              >
                <p className="font-medium text-sm">{result.name}</p>
                <p className="text-xs text-muted-foreground">
                  {result.address_line1}, {result.suburb} {result.state} {result.postcode}
                </p>
                {result.phone && <p className="text-xs text-muted-foreground">{result.phone}</p>}
              </button>
            ))}
          </div>
        )}

        {lookup.data?.results && lookup.data.results.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">No results found.</p>
        )}
      </DialogContent>
    </Dialog>
  )
}
